/**
 * RESET · Ultra — Dynamic Plan Generator
 *
 * Produces a fully periodised training plan based on race params
 * and user preferences. Replaces the fixed 3-session plan.
 *
 * Algorithm follows the spec exactly:
 *   Phase structure → Volume ramp → Session assignment → HR zones
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/* ─── helpers ─────────────────────────────────────────────── */

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

function startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function startOfWeek(d) {
  const r = startOfDay(d);
  const day = r.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday-start
  r.setDate(r.getDate() + diff);
  return r;
}

// Calendar-day addition, DST-safe. Adding n * 86400000 ms breaks across a
// DST transition (e.g. Germany's Oct-25 fall-back): +7 days of ms lands an
// hour early → 23:00 the previous wall-clock day → toLocalDateString then
// rolls the whole session back one calendar day, shifting every post-DST
// week off its availableDays. setDate() operates on wall-clock calendar
// days and is immune to that. (Verified: 50K/trail plan across the Nov DST
// boundary previously placed the long run on Tue instead of the pinned Wed.)
function addDays(d, n) { const r = new Date(d); r.setHours(0, 0, 0, 0); r.setDate(r.getDate() + n); return r; }

function dayName(d) {
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(d).getDay()];
}

/**
 * Serialize a Date to a local YYYY-MM-DD string (no timezone shift).
 *
 * IMPORTANT: do NOT use `date.toISOString().split('T')[0]` — that converts
 * to UTC first, which shifts dates by ~1 day in non-zero UTC offsets.
 * That bug (combined with the inconsistent training-schedule UI) is what
 * caused sessions to appear on "Monday" even when Monday was excluded
 * from availableDays — local-Tuesday-00:00 in CEST became UTC-Monday-22:00,
 * and `.toISOString()` truncated it to Monday's date string.
 */
function toLocalDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/* ─── phase structure ─────────────────────────────────────── */

/**
 * Compute phase boundaries.
 *
 * Methodology reference: `/docs/training_methodology.md` §Pillar 3.
 *
 * The four phases (Base, Build, Specific, Taper) are the ONLY top-level
 * phase values. Recovery is NOT a phase — it is a week-level modifier that
 * applies within Base and Build. A recovery week keeps its parent phase
 * string and carries an `isRecoveryWeek: true` flag. Downstream consumers
 * use that flag to reduce volume (×0.75 — the methodology's −25%), soften
 * intensity, and block B2B / hiking modifiers.
 *
 * Phase allocation (%, of TOTAL plan including taper):
 *   Base:     ~35%  — aerobic foundation
 *   Build:    ~30%  — threshold + quality introduction
 *   Specific: ~20%  — race-specificity (B2B peaks, terrain work)
 *   Taper:    ~15%  — 2–3 weeks depending on race distance
 *
 * Recovery week cadence: every 4th week (0-indexed: 3, 7, 11, …), in BASE
 * and BUILD only (§Pillar 3: "every 3rd or 4th week within Base and Build").
 * Specific-phase weeks are never recovery weeks — that block is where the
 * race-specific peak work sits.
 */
function computePhases(totalWeeks, raceDistanceKm) {
  // Taper length by distance (§Pillar 3): 3 wk ≥ 80K, 2 wk ultra/marathon,
  // 1 wk for ≤ half-marathon ("Road to Ultra" speed goals — a 10-day taper
  // is the practitioner norm, weeks are the plan's granularity).
  const taperWeeks = raceDistanceKm >= 80 ? 3 : raceDistanceKm > 21.2 ? 2 : 1;

  const baseFrac = 0.35;
  const buildFrac = 0.30;

  const baseEnd   = Math.max(1, Math.floor(totalWeeks * baseFrac));
  const buildEnd  = Math.max(baseEnd + 1, baseEnd + Math.floor(totalWeeks * buildFrac));

  const phases = [];
  for (let w = 0; w < totalWeeks; w++) {
    const isTaper = w >= totalWeeks - taperWeeks;

    if (isTaper) {
      const taperIdx = w - (totalWeeks - taperWeeks); // 0, 1, 2
      phases.push({ weekNumber: w + 1, phase: 'taper', isRecoveryWeek: false, taperIdx });
      continue;
    }

    const phase = w < baseEnd ? 'base' : w < buildEnd ? 'build' : 'specific';
    const isRecoveryWeek = w > 0 && w % 4 === 3 && phase !== 'specific';

    phases.push({ weekNumber: w + 1, phase, isRecoveryWeek });
  }
  return phases;
}

/** §Pillar 3 — recovery weeks drop volume by 25%. */
const RECOVERY_WEEK_FACTOR = 0.75;

/** §"Short-distance goals" — the long run never drops below this. */
const LONG_RUN_FLOOR_KM = 12;

/* ─── volume ramp ─────────────────────────────────────────── */

/**
 * Peak weekly volume target, aligned to methodology §Pillar 4 practitioner
 * benchmarks (Roche / Koop / Moehl consensus):
 *
 *   Race   | methodology range  | this function returns
 *   -------|--------------------|----------------------
 *   50K    | 60–80 km           | 65 km   (×1.30)
 *   80K    | 90–105 km          | 92 km   (×1.15)
 *   100K   | 100–130 km         | 115 km  (×1.15)
 *   160K   | 115–160 km         | 128 km  (×0.80)
 *
 * Previous version returned values WELL BELOW the methodology minimum
 * (e.g. 75 km for 100K vs 100–130 required) — plans were producing peaks
 * ~25% of what the athlete actually needed. Fixed to sit at the lower-mid
 * of each range: methodology-compliant without forcing the top of the range
 * on runners who aren't elite.
 */
function computePeakVolume(raceDistanceKm, baselineWeeklyKm = 0) {
  if (raceDistanceKm >= 160) return raceDistanceKm * 0.80;
  if (raceDistanceKm >= 80)  return raceDistanceKm * 1.15;
  if (raceDistanceKm >= 45)  return raceDistanceKm * 1.30;
  // ── Short-distance mode (5K–marathon, §"Short-distance goals") ──
  // Race distance is a useless volume anchor below the ultra range (1.4× a
  // 5K is 7 km/week). Peak is BASELINE-driven — the runner's median week
  // +25% — clamped to a practitioner band per distance. Without a baseline
  // the floor applies (cold start).
  const band = shortDistanceBand(raceDistanceKm);
  const base = Math.max(0, Number(baselineWeeklyKm) || 0);
  if (base <= 0) return band.floor;
  return clamp(base * 1.25, band.floor, band.cap);
}

/** Practitioner weekly-volume bands for sub-ultra goals (km/week). */
function shortDistanceBand(raceDistanceKm) {
  if (raceDistanceKm <= 5.5)  return { floor: 30, cap: 60 };
  if (raceDistanceKm <= 10.5) return { floor: 35, cap: 70 };
  if (raceDistanceKm <= 21.2) return { floor: 45, cap: 85 };
  return { floor: 55, cap: 100 };                    // marathon
}

/** Sub-ultra goal? (drives taper, long-run cap, speed block, no TOF) */
function isShortDistance(raceDistanceKm) {
  return Number(raceDistanceKm) > 0 && Number(raceDistanceKm) < 45;
}

/** Speed-block interval prescription by race distance (taxonomy #8). */
function intervalPurpose(raceDistanceKm) {
  if (raceDistanceKm <= 5.5)  return 'Speed block. 6 × 800 m at 5K effort, 2 min easy jog between. Warm up 15 min, cool down 10 min.';
  if (raceDistanceKm <= 10.5) return 'Speed block. 5 × 1 km at 10K effort, 90 s easy jog between. Warm up 15 min, cool down 10 min.';
  if (raceDistanceKm <= 21.2) return 'Speed block. 3 × 3 km at half-marathon effort, 3 min easy jog between. Warm up 15 min, cool down 10 min.';
  return 'Marathon-pace block. 3 × 4 km at goal marathon pace, 1 km easy between. Warm up 15 min, cool down 10 min.';
}

/* ─── readiness gate ──────────────────────────────────────────
 *
 * Methodology reference: `/docs/training_methodology.md` §"Readiness gate".
 *
 * THE ANTI-GARMIN RULE. A plan generator must not silently emit a plan whose
 * opening week demands a volume the runner cannot safely reach. Garmin's 100K
 * plan did exactly that — week 1 jumped far above the runner's base with no
 * assessment of whether the ramp was survivable.
 *
 * Rule: a plan ramps +10%/week to a methodology peak. Backsolving, the FIRST
 * week must be `peakKm / 1.10^(rampWeeks-1)` — call it `requiredStart`. If the
 * runner's 4-week median is so low that even a generous 1.5× stretch can't
 * reach `requiredStart`, the plan is refused and three honest options are
 * surfaced instead:
 *   (a) move the race later  — enough weeks that the ramp works
 *   (b) drop to a shorter distance on the same date
 *   (c) accept a finish-only plan — a safe ramp from the real baseline, honest
 *       that the entered goal time is out of reach.
 *
 * `assessReadiness` is PURE and exported so the UI can gate BEFORE generation;
 * `generatePlan` also calls it as a server-side backstop.
 */

// Standard race distances, longest→shortest. Used to suggest a safer distance.
const STANDARD_DISTANCES = [
  { label: '100M', km: 160.934 },
  { label: '100K', km: 100 },
  { label: '50M',  km: 80.467 },
  { label: '50K',  km: 50 },
  { label: 'Marathon', km: 42.195 },
];

/** Count of non-taper, non-recovery weeks for a given timeline — the "ramp". */
function rampWeekCount(totalWeeks, raceDistanceKm) {
  return computePhases(totalWeeks, raceDistanceKm)
    .filter((p) => p.phase !== 'taper' && !p.isRecoveryWeek).length;
}

/** requiredStart — the week-1 volume a +10%/wk ramp needs to hit peak. */
function requiredStartVolume(totalWeeks, raceDistanceKm, baselineWeeklyKm = 0) {
  const ramp = rampWeekCount(totalWeeks, raceDistanceKm);
  const peakKm = computePeakVolume(raceDistanceKm, baselineWeeklyKm);
  return peakKm / Math.pow(1.10, Math.max(1, ramp - 1));
}

/**
 * Projected peak for a finish-only plan: a SAFE forward ramp from the real
 * baseline (+10%/wk), never the methodology peak. Capped at 75% of the
 * methodology peak so it stays honestly sub-competitive.
 */
function finishOnlyPeakVolume(baselineWeeklyKm, totalWeeks, raceDistanceKm) {
  const ramp = rampWeekCount(totalWeeks, raceDistanceKm);
  const start = Math.max(1, Number(baselineWeeklyKm) || 0) * 1.10;
  const projected = start * Math.pow(1.10, Math.max(0, ramp - 1));
  return Math.round(Math.min(projected, computePeakVolume(raceDistanceKm, baselineWeeklyKm) * 0.75) * 10) / 10;
}

/**
 * Assess whether a plan's ramp is safely achievable from the runner's baseline.
 *
 * @param {Object} p
 * @param {number} p.baselineWeeklyKm  4-week median weekly km (userBaseline).
 * @param {number} p.raceDistanceKm
 * @param {number} p.weeksAvailable    whole weeks between now and race.
 * @param {number} [p.now]             epoch ms — injected for testing.
 * @param {boolean} [p.baselineKnown]  true when baselineWeeklyKm comes from a
 *   real measured baseline (userBaseline object). A measured 0 km/wk is a
 *   signal — the runner is idle — not an absent baseline, so it is gated
 *   (§"Readiness gate": the gate exists for exactly this runner). Only a
 *   caller with no baseline at all takes the ungated cold-start path.
 * @returns {Object} readiness verdict + the three options when not ready.
 */
function assessReadiness({ baselineWeeklyKm, raceDistanceKm, weeksAvailable, now, baselineKnown = false } = {}) {
  const base = Math.max(0, Number(baselineWeeklyKm) || 0);
  const dist = Number(raceDistanceKm) || 0;
  const totalWeeks = Math.max(1, Math.round(Number(weeksAvailable) || 0));
  const nowMs = Number.isFinite(now) ? now : Date.now();

  const peakKm = computePeakVolume(dist, base);
  const requiredStart = requiredStartVolume(totalWeeks, dist, base);
  const safeStart = base * 1.5;            // the 1.5× stretch ceiling
  const ready = base > 0 && requiredStart <= safeStart;
  const stretchFactor = base > 0
    ? Math.round((requiredStart / (base * 1.10)) * 100) / 100
    : null;

  const verdict = {
    ready,
    hasBaseline: base > 0 || baselineKnown === true,
    stretchFactor,
    baselineWeeklyKm: Math.round(base * 10) / 10,
    requiredStart: Math.round(requiredStart * 10) / 10,
    safeStart: Math.round(safeStart * 10) / 10,
    peakKm: Math.round(peakKm * 10) / 10,
    weeksAvailable: totalWeeks,
    raceDistanceKm: dist,
  };

  if (ready) return verdict;

  // ── Not ready — compute the three options. ──

  // (a) Later date: smallest timeline where the ramp works from this baseline.
  let laterDate = null;
  if (base > 0) {
    for (let wk = totalWeeks + 1; wk <= 78; wk++) {
      if (requiredStartVolume(wk, dist, base) <= base * 1.5) {
        laterDate = {
          suggestedWeeks: wk,
          suggestedRaceDate: new Date(nowMs + wk * 7 * DAY_MS)
            .toISOString().slice(0, 10),
        };
        break;
      }
    }
  }

  // (b) Shorter distance: longest standard ultra below the goal that the
  // current timeline CAN safely ramp to. Only ultra distances (≥ 50K) are
  // offered — suggesting a 10K to a 100K hopeful is not useful coaching.
  let shorterDistance = null;
  for (const d of STANDARD_DISTANCES) {
    if (d.km >= dist || d.km < 50) continue;
    if (requiredStartVolume(totalWeeks, d.km, base) <= base * 1.5) {
      shorterDistance = { label: d.label, km: d.km };
      break;
    }
  }

  // (c) Finish-only: always available. A safe ramp from the real baseline.
  const finishOnly = {
    available: true,
    projectedPeakKm: finishOnlyPeakVolume(base, totalWeeks, dist),
    finishWindowNote:
      'A finish-focused plan trains you to complete the distance, not to hit ' +
      'the entered goal time. Expect a slower, conservative finish.',
  };

  // NB: assign rather than object-spread. Object spread ({ ...verdict }) makes
  // babel inject an `import` of its spread helper, which flips this CommonJS
  // file to an ES module in webpack's view and breaks the client mirror's
  // `module.exports`. See CLAUDE.md §"Generator mirror".
  verdict.options = { laterDate, shorterDistance, finishOnly };
  return verdict;
}

/**
 * Build the weekly km targets.
 *
 * Methodology §Pillar 4 + §Pillar 3:
 *   - Peak volume hits the race-distance benchmark (see computePeakVolume).
 *   - Progression cap: +10% per non-recovery training week.
 *   - Recovery weeks: 75% of the preceding real training week (−25%).
 *   - Taper: 70% → 50% → 30% of the ACTUAL achieved peak (not the cap).
 *
 * Two bugs fixed in this revision:
 *   1. Start volume was `currentWeeklyKm × 1.10`. If the user's current
 *      weekly km was low (e.g. 15), the ramp never reached the methodology
 *      peak before taper — plans produced 25%-of-target volumes.
 *      Now the starting volume is BACKSOLVED from the peak: whatever
 *      volume is needed so +10%/week compounds to `peakKm` at the last
 *      non-recovery training week. If the user's current is higher than
 *      that anchored start, we use theirs. This is methodology-driven
 *      volume, not user-volume-driven.
 *   2. Taper was computed as `peakKm × pct`. `peakKm` is the theoretical
 *      cap; if the ramp never reached it, taper could end up HIGHER than
 *      the actual peak training week (anti-pattern #6 — monotonic increase
 *      into race week). Now taper is `actualPeak × pct` where actualPeak
 *      is the max volume of the non-taper, non-recovery weeks. Always
 *      descends from peak.
 */
function computeWeeklyVolumes(phases, baselineWeeklyKm, raceDistanceKm, { goalMode = 'standard' } = {}) {
  const peakKm = computePeakVolume(raceDistanceKm, baselineWeeklyKm);
  const totalWeeks = phases.length;
  const volumes = new Array(totalWeeks);
  const isFinishOnly = goalMode === 'finish-only';

  // Number of non-recovery, non-taper weeks — the "ramp zone".
  const rampWeeksCount = phases.filter(
    (p) => p.phase !== 'taper' && !p.isRecoveryWeek
  ).length;

  // Starting volume + peak ceiling differ by goal mode:
  //
  //  standard     — BACKSOLVE the start so a +10%/wk ramp lands on the
  //                 methodology peak at the last training week:
  //                 startKm × 1.10^(N-1) = peakKm. If the runner's own
  //                 baseline×1.10 is higher, use theirs. The readiness gate
  //                 (assessReadiness) has already refused any case where
  //                 this backsolved start is an unsafe leap.
  //
  //  finish-only  — NO backsolve. A finish-focused plan must never chase the
  //                 methodology peak from a base that can't support it. The
  //                 ramp goes FORWARD from the real baseline (+10%/wk) and
  //                 tops out wherever it naturally lands, capped at 75% of
  //                 the methodology peak. Honest volume over aspirational
  //                 volume — see §"Readiness gate" option (c).
  const rampFactor = Math.pow(1.10, Math.max(1, rampWeeksCount - 1));
  const peakCeiling = isFinishOnly ? peakKm * 0.75 : peakKm;
  const baselineStart = Math.max(0, Number(baselineWeeklyKm) || 0) * 1.10;
  const startKm = isFinishOnly
    ? Math.min(baselineStart, peakCeiling)
    : Math.max(peakKm / rampFactor, baselineStart);

  // First pass: fill training weeks. Recovery weeks are 65% of the previous
  // training week. Taper weeks are placeholders until pass 2.
  let rampVol = Math.min(startKm, peakCeiling);
  let prevTrainingVol = rampVol;
  for (let w = 0; w < totalWeeks; w++) {
    const p = phases[w];
    if (p.phase === 'taper') { volumes[w] = 0; continue; }
    if (p.isRecoveryWeek) {
      volumes[w] = Math.round(prevTrainingVol * RECOVERY_WEEK_FACTOR * 10) / 10;
    } else {
      volumes[w] = Math.round(rampVol * 10) / 10;
      prevTrainingVol = volumes[w];
      rampVol = Math.min(rampVol * 1.10, peakCeiling);
    }
  }

  // Pass 2: taper = pct of the ACTUAL peak achieved (max of training weeks).
  const trainingVolumes = volumes.filter(
    (_, i) => phases[i].phase !== 'taper' && !phases[i].isRecoveryWeek
  );
  const actualPeak = trainingVolumes.length ? Math.max(...trainingVolumes) : 0;
  const taperPcts = [0.70, 0.50, 0.30];
  for (let w = 0; w < totalWeeks; w++) {
    const p = phases[w];
    if (p.phase === 'taper') {
      const pct = taperPcts[p.taperIdx] || 0.30;
      volumes[w] = Math.round(actualPeak * pct * 10) / 10;
    }
  }

  return volumes;
}

/* ─── duration estimation ─────────────────────────────────── */

function terrainMultiplier(terrain) {
  if (terrain === 'trail') return 1.3;
  if (terrain === 'mixed') return 1.15;
  return 1.0; // road
}

function estimateDurationHours(km, targetPaceMinPerKm, terrain) {
  const mult = terrainMultiplier(terrain);
  return (km * mult * targetPaceMinPerKm) / 60;
}

/* ─── elevation targets ───────────────────────────────────── */

function weeklyElevationTarget(phase, terrain, isRecoveryWeek = false) {
  if (terrain !== 'trail' && terrain !== 'mixed') return 0;
  let base;
  switch (phase) {
    case 'base':     base = 300;  break;
    case 'build':    base = 600;  break;
    case 'specific': base = 1200; break;
    case 'taper':    base = 0;    break;
    default:         base = 0;
  }
  // Recovery weeks halve the phase's elevation target.
  return isRecoveryWeek ? Math.round(base * 0.5) : base;
}

/* ─── session display labels ──────────────────────────────────
 * Canonical human-readable labels for each session type. Mirrors the
 * frontend's SESSION_TYPE_LABELS in src/App.js — keep these in sync if
 * a new session type is added on the server.
 * Used by formatSessionTypeLabel() (also exported) for any server-side
 * code that needs to produce human-readable session names. */
const SESSION_TYPE_LABELS = {
  'b2b-day1':      'B2B Day 1',
  'b2b-day2':      'B2B Day 2',
  'time-on-feet':  'Time on Feet',
  'recovery-easy': 'Recovery Easy',
  'vertical':      'Vertical',
  'easy':          'Easy',
  'tempo':         'Tempo',
  'long':          'Long',
  'hike':          'Hike',
  'race':          'Race',
  'intervals':     'Intervals',
};
function formatSessionTypeLabel(type) {
  if (!type) return '';
  const key = String(type).toLowerCase();
  if (SESSION_TYPE_LABELS[key]) return SESSION_TYPE_LABELS[key];
  return key.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─── session HR zones & purposes ─────────────────────────── */

const SESSION_HR_ZONES = {
  'easy':          'Z2',
  'tempo':         'Z3-Z4',
  'long':          'Z2-Z3',
  'b2b-day1':      'Z2-Z3',
  'b2b-day2':      'Z2',
  'time-on-feet':  'Z2',
  'vertical':      'Z2-Z3',
  'hike':          'Z2',
  'recovery-easy': 'Z2',
  'intervals':     'Z4-Z5',
  'race':          'Race',
};

const SESSION_PURPOSES = {
  'easy':          'Aerobic base. Run easy, hold Z2 HR throughout.',
  'tempo':         'Lactate threshold. Comfortably hard effort, Z3-Z4 HR. Optional: finish with 4-6 × 15-second relaxed strides to keep leg speed sharp — no need to log them separately.',
  'long':          'Endurance. Time on your feet, Z2 HR, no pace pressure.',
  'b2b-day1':      'Back-to-back Day 1. Strong effort, build fatigue deliberately.',
  'b2b-day2':      'Back-to-back Day 2. Run on tired legs. This is the adaptation.',
  'time-on-feet':  'Stay moving for the full duration. Pace is irrelevant. Z2 HR only.',
  'vertical':      'Elevation is the goal. Hike the steep parts. Accumulate the meters.',
  'hike':          'Active recovery. Trail hike, easy pace, enjoy it.',
  'recovery-easy': 'Recovery week. Genuinely easy. Protect the adaptation.',
  'intervals':     'Speed block. Short hard repetitions with full recovery between — see the session note.',
};

/* ─── per-session pace multipliers ─────────────────────────────
 * Threshold-anchored: all training paces are a multiplier on THRESHOLD
 * pace, not ultra race pace. The physiological reality is that every
 * training zone (easy, long, recovery, b2b) scales off threshold — ultra
 * race pace is a deeply aerobic effort that's SLOWER than every training
 * pace, so it's useless as an anchor (multipliers would produce walking-
 * pace "Easy" runs and conversational-pace "Tempo" efforts).
 *
 * Values are (session_pace / threshold_pace). >1 = slower than threshold.
 * Standard endurance-coaching table — rough but consistent across runners.
 *
 * Methodology: `/docs/training_methodology.md` §Pillar 1 "Polarized".
 *
 * NOT IN THIS TABLE:
 *   - 'vertical' — no pace prescription; hill terrain is too variable,
 *     run by effort per §151.
 *   - 'hike' / 'time-on-feet' — duration-based, see distributeSessions.
 *   - 'race' — not a training session type.
 */
const SESSION_THRESHOLD_MULTIPLIER = {
  'tempo':         1.00,  // threshold itself (1-hour sustainable pace)
  'b2b-day1':      1.25,
  'long':          1.20,
  'easy':          1.35,  // conversational, aerobic
  'b2b-day2':      1.40,  // fatigued legs, slower than day 1
  'recovery-easy': 1.45,  // genuinely easy
  'intervals':     0.92,  // speed block (macroPlan.js): reps faster than threshold, average incl. recoveries
};

/* ─── duration-only sessions: pace used to ESTIMATE distance ─────
 * time-on-feet and hike prescribe a duration, not a distance, so they carry
 * `plannedKm: null` and the UI presents them as time. But §Pillar 4 says the
 * week's `plannedKm` is "the number every other surface reads (chart, Weekly
 * Load, adherence)" — and a week whose biggest session is a 3h50m Z2 effort
 * is not a 34 km week. Omitting it understated the Specific block by ~49 %
 * and made an athlete who ran exactly the plan read as 197 % of target.
 *
 * So the week total includes an ESTIMATE for these sessions, derived from the
 * plan's own threshold pace. The multipliers are deliberately at/below the
 * slowest running pace in the table above, so the estimate is conservative:
 * it never inflates a week's volume and never flatters the readiness gate or
 * the ramp (§Readiness gate, §Pillar 4).
 */
const DURATION_SESSION_PACE_MULTIPLIER = {
  'time-on-feet': 1.45,  // matches 'recovery-easy', the slowest running pace prescribed
  'hike':         2.10,  // hiking, not running — ~10:30/km off a 5:00/km threshold
};

/** Estimated km for a duration-only session. null when it cannot be derived. */
function estimateKmFromDuration(type, durationMin, thresholdPaceSec) {
  const mult = DURATION_SESSION_PACE_MULTIPLIER[type];
  if (!mult) return null;
  const mins = Number(durationMin);
  const thr = Number(thresholdPaceSec);
  if (!(mins > 0) || !(thr > 0)) return null;
  return (mins * 60) / (thr * mult);
}

/* ─── threshold pace estimation (cold start, no Strava data) ─────
 * Without observed training data, we derive threshold pace by projecting
 * the user's ultra goal time back to a half-marathon time via Riegel:
 *
 *   T_half = T_goal × (21.0975 / raceDistanceKm) ^ 1.30
 *
 * The 1.30 exponent is steeper than the classic 1.06 because Riegel-1.06
 * systematically UNDERESTIMATES a well-trained ultra runner's shorter-
 * distance capability (their aerobic base is strong but they usually
 * retain meaningful speed). 1.30 calibrates to well-trained amateur ultra
 * runners — e.g. a 12:00/100K goal yields ~4:31/km threshold, which is
 * physiologically sensible for that fitness level.
 *
 * Precision caveat: any single-parameter model is wrong for some runners.
 * Cold-start is a best-effort anchor; once Strava data arrives, the
 * client-side helper replaces this with the user's OBSERVED fastest pace
 * over recent 3–15 km runs (see derivePlannedDurationMin in src/App.js).
 *
 * NO separate terrain multiplier is applied here or on the derived
 * training paces: the user's goal time already encodes terrain effort
 * (a 100K trail @ 14:30 goal implies a different athlete than 100K road
 * @ 14:30), so double-multiplying produces absurd values.
 *
 * Methodology: `/docs/training_methodology.md` §Pillar 1 + §151 (tempo
 * prescription is HR/RPE; pace is a display hint).
 */
const HALF_MARATHON_KM = 21.0975;
const THRESHOLD_RIEGEL_EXPONENT = 1.30;
function thresholdPaceSecPerKm(raceDistanceKm, targetFinishSec) {
  if (!(raceDistanceKm > 0) || !(targetFinishSec > 0)) return null;
  const halfSec = targetFinishSec * Math.pow(HALF_MARATHON_KM / raceDistanceKm, THRESHOLD_RIEGEL_EXPONENT);
  return halfSec / HALF_MARATHON_KM;
}

/* ─── session assignment ──────────────────────────────────────
 *
 * The week is built in THREE distinct passes — do NOT mix them:
 *
 *   1. `baseSessionTypes(runsPerWeek)` — pure base: 1 Long + N Easy + 1 Tempo
 *      (+ optional Recovery-Easy at runsPerWeek=5). Phase-agnostic.
 *
 *   2. `applyPhaseTypeModifiers(base, phase, opts)` — type-level transforms
 *      that don't depend on day placement:
 *        peak:         long → time-on-feet
 *        recovery:     tempo → recovery-easy; easy → recovery-easy
 *                      (long stays so the latest-day anchor still resolves;
 *                       the week's volume scales × 0.65 in computeWeeklyVolumes)
 *        vertical on:  tempo → vertical (build/peak, trail/mixed terrain only)
 *
 *   3. `placeAndApplyPlacementModifiers(types, sortedDays, phase, opts)` —
 *      day placement + placement-dependent modifiers (B2B, Hikes). See that
 *      function's docstring for the full rule set.
 *
 * CRITICAL RULE — preserved by the separation above:
 *   Modifiers NEVER overwrite the base. They transform exactly one slot per
 *   flag (B2B converts the long+next pair; Hike swaps ONE easy). The old bug
 *   where `sessionTypesForWeek` returned ['easy', 'b2b-day1', 'b2b-day2']
 *   (no Long at all) or had Hikes replace multiple easies is gone.
 */
const LONG_ANCHOR_TYPES = new Set(['long', 'time-on-feet', 'b2b-day1']);
const TEMPO_TYPES = new Set(['tempo', 'vertical', 'intervals']);
const EASY_TYPES = new Set(['easy', 'recovery-easy']);

/**
 * Pure base structure for a week, by runsPerWeek.
 * Order is canonical (Long, Easy…, Tempo, [Recovery]) — slot placement is
 * done later by type, not by array position.
 */
function baseSessionTypes(runsPerWeek, goalMode = 'standard') {
  const n = Math.max(3, Math.min(5, Number(runsPerWeek) || 3));
  // Finish-only plans carry NO quality work — the tempo slot becomes easy.
  // The 20% intensity portion of §Pillar 1 is deliberately dropped: a runner
  // who can't safely ramp to the distance should not also be absorbing
  // threshold load. See §"Readiness gate" option (c).
  const quality = goalMode === 'finish-only' ? 'easy' : 'tempo';
  if (n === 3) return ['long', 'easy', quality];
  if (n === 4) return ['long', 'easy', 'easy', quality];
  // n === 5
  return ['long', 'easy', 'easy', quality, 'recovery-easy'];
}

/**
 * Phase- and terrain-dependent TYPE transforms. No day placement here.
 * Mutations are local to the returned array; caller's input is untouched.
 *
 * Phase semantics per §Pillar 3:
 *   - 'specific' (the race-specificity block) is where the long becomes
 *     race-effort time-on-feet and where elevation/hill work dominates the
 *     intensity portion.
 *   - Recovery is a WEEK-LEVEL modifier — `isRecoveryWeek=true` de-intensifies
 *     the same parent-phase structure (it is not a phase itself).
 */
function applyPhaseTypeModifiers(baseTypes, phase, { includeVertical = false, terrain = 'road', isRecoveryWeek = false, goalMode = 'standard', shortDistance = false } = {}) {
  const types = [...baseTypes];

  // §"Short-distance goals": the quality slot becomes intervals in Build +
  // Specific (the speed block). Base keeps tempo; recovery weeks and
  // finish-only plans never carry intervals.
  if (shortDistance && !isRecoveryWeek && goalMode !== 'finish-only' && (phase === 'build' || phase === 'specific')) {
    const i = types.indexOf('tempo');
    if (i >= 0) types[i] = 'intervals';
  }
  const isTrail = terrain === 'trail' || terrain === 'mixed' || terrain === 'mountain';
  const isFinishOnly = goalMode === 'finish-only';

  // Long → time-on-feet. Standard plans do this in the Specific phase only
  // (race-pace simulation). Finish-only plans ALSO do it through Build: the
  // goal is completion endurance, so time-on-feet is front-loaded rather
  // than reserved for race-specificity. Skipped on recovery weeks either way.
  const longToTimeOnFeet = !shortDistance && (phase === 'specific' || (isFinishOnly && phase === 'build'));
  if (longToTimeOnFeet && !isRecoveryWeek) {
    const i = types.indexOf('long');
    if (i >= 0) types[i] = 'time-on-feet';
  }

  // Recovery week: de-intensify. Tempo and easies drop to recovery-easy.
  // Long stays so the latest-day anchor still resolves — the weekly volume
  // is scaled × 0.65 in computeWeeklyVolumes, so the long is shorter too.
  if (isRecoveryWeek) {
    for (let i = 0; i < types.length; i++) {
      if (types[i] === 'tempo') types[i] = 'recovery-easy';
      else if (types[i] === 'easy') types[i] = 'recovery-easy';
    }
  }

  // Vertical focus: tempo becomes a hill/vertical session.
  // Allowed in build + specific on trail/mixed/mountain terrain (hill work
  // can replace the tempo slot there per §Pillar 4 "Elevation specificity").
  // Skipped on recovery weeks — tempo was already demoted to recovery-easy
  // above, and hill intensity belongs in real training weeks.
  if (includeVertical && isTrail && !isRecoveryWeek && (phase === 'build' || phase === 'specific')) {
    const i = types.indexOf('tempo');
    if (i >= 0) types[i] = 'vertical';
  }

  return types;
}

/**
 * §"Short-distance goals" long-run cap: below the half 1.5 × race distance
 * (min 12 km); the half (and anything up to the marathon) 90 %; marathon and
 * up the §Pillar 4 75 % cap. The brackets are strict on the race distances
 * themselves — 21.0975 is "half", 42.195 is "marathon and up" (the old
 * `< 21.2` / `< 42.2` cut-offs put both in the bracket below).
 */
function longRunCapKm(raceDistanceKm) {
  const d = Number(raceDistanceKm) || 0;
  if (d < 21) return Math.max(d * 1.5, 12);
  if (d < 42) return d * 0.9;
  return d * 0.75;
}

/**
 * §Pillar 4 — peak SINGLE long run per goal distance (practitioner
 * benchmarks, mid-range): 50K 31 km, 50M 44 km, 100K 50 km, 100M 55 km.
 * Below the ultra range the long-run cap itself is the peak.
 */
function benchmarkPeakLongKm(raceDistanceKm) {
  const d = Number(raceDistanceKm) || 0;
  if (d >= 160) return 55;
  if (d >= 100) return 50;
  if (d >= 80) return 44;
  if (d >= 45) return 31;
  return longRunCapKm(d);
}

/** Peak long run for a plan — benchmark, never above the §Pillar 4 cap. */
function peakLongRunKm(raceDistanceKm) {
  return Math.min(longRunCapKm(raceDistanceKm), benchmarkPeakLongKm(raceDistanceKm));
}

/**
 * Long-run progression (§Pillar 3: "Long Run progression is not linear: it
 * ramps up in Base, peaks mid-Specific, drops in Taper"; §Pillar 3 table:
 * taper long run ~40% of peak).
 *
 * The long run gets its OWN ascending curve instead of a flat 45% share of
 * whatever the week happens to be — that share made base-phase long runs the
 * longest of the plan and let taper week 1 outrun every Specific week.
 *
 *   week 0        → 45% of the peak long run
 *   peak week     → 100% (middle of the Specific block)
 *   after peak    → eases back to 85% at the last training week
 *   recovery week → ×0.75, like the week's volume
 *   taper         → 55/40/25% of peak (3 wk), 50/40% (2 wk), 40% (1 wk)
 *
 * Per-week volume still caps the result inside distributeSessions.
 */
function computeLongRunTargets(phases, raceDistanceKm) {
  const peak = peakLongRunKm(raceDistanceKm);
  const n = phases.length;
  const idxNonTaper = [];
  const idxSpecific = [];
  for (let w = 0; w < n; w++) {
    if (phases[w].phase === 'taper') continue;
    idxNonTaper.push(w);
    if (phases[w].phase === 'specific' && !phases[w].isRecoveryWeek) idxSpecific.push(w);
  }
  const lastTraining = idxNonTaper.length ? idxNonTaper[idxNonTaper.length - 1] : 0;
  const peakIdx = idxSpecific.length
    ? idxSpecific[Math.floor((idxSpecific.length - 1) / 2)]
    : lastTraining;
  const taperCount = phases.filter((p) => p.phase === 'taper').length;
  const taperFracs = taperCount >= 3 ? [0.55, 0.40, 0.25] : taperCount === 2 ? [0.50, 0.40] : [0.40];

  const START_FRAC = 0.45;
  const out = new Array(n);
  for (let w = 0; w < n; w++) {
    const p = phases[w];
    let frac;
    if (p.phase === 'taper') {
      frac = taperFracs[p.taperIdx] != null ? taperFracs[p.taperIdx] : taperFracs[taperFracs.length - 1];
    } else if (w <= peakIdx) {
      frac = peakIdx > 0 ? START_FRAC + (1 - START_FRAC) * (w / peakIdx) : 1;
    } else {
      const span = lastTraining - peakIdx;
      frac = 1 - 0.15 * (span > 0 ? (w - peakIdx) / span : 0);
    }
    if (p.phase !== 'taper' && p.isRecoveryWeek) frac *= RECOVERY_WEEK_FACTOR;
    // Floor: a long run never drops below 12 km (§"Short-distance goals"
    // keeps the long run at "min 12 km" for sub-half goals; for longer goals
    // the floor is far below the curve anyway).
    out[w] = Math.round(Math.max(peak * frac, Math.min(peak, LONG_RUN_FLOOR_KM)) * 10) / 10;
  }
  return out;
}

/**
 * Distribute km across session types for a week.
 *
 * Long-run cap (§Pillar 4): no single long session exceeds 75% of race
 * distance. Applies to 'long' type; 'time-on-feet' is duration-based so
 * the cap is implicit.
 *
 * Returns array of { type, plannedKm, plannedDuration, plannedElevation }
 */
function capEasyOverflow(sessions, weeklyKm, raceDistanceKm, longIsOnCurve = false) {
  const round1 = (x) => Math.round(x * 10) / 10;
  const EASY_SHARE = { 'easy': 0.28, 'recovery-easy': 0.45 };
  const easies = sessions.filter((s) => EASY_SHARE[s.type] != null && s.plannedKm != null);
  if (!easies.length) return;
  const longCap = raceDistanceKm > 0 ? longRunCapKm(raceDistanceKm) : Infinity;
  const d1 = sessions.find((s) => s.type === 'b2b-day1' && s.plannedKm != null);
  const d2 = sessions.find((s) => s.type === 'b2b-day2' && s.plannedKm != null);
  const long = sessions.find((s) => s.type === 'long' && s.plannedKm != null);

  // 1. Cap each easy at its share of the week.
  let excess = 0;
  for (const s of easies) {
    const cap = weeklyKm * EASY_SHARE[s.type];
    if (s.plannedKm > cap) { excess += s.plannedKm - cap; s.plannedKm = round1(cap); }
  }
  // 2. Excess → long anchor, within the long-run cap. Skipped when the long
  //    run follows its own progression curve (§Pillar 3) — the anchor is the
  //    curve's value, not a dumping ground for leftover volume.
  const absorb = () => {
    if (excess <= 0.05 || longIsOnCurve) return;
    if (d1 && d2) {
      const pair = d1.plannedKm + d2.plannedKm;
      const room = Math.max(0, longCap / 0.60 - pair);
      const add = Math.min(excess, room);
      if (add > 0) {
        d1.plannedKm = round1((pair + add) * 0.40);
        d2.plannedKm = round1((pair + add) * 0.60);
        excess -= add;
      }
    } else if (long) {
      const room = Math.max(0, Math.min(longCap, weeklyKm * 0.55) - long.plannedKm);
      const add = Math.min(excess, room);
      if (add > 0) { long.plannedKm = round1(long.plannedKm + add); excess -= add; }
    }
  };
  absorb();
  // 3. Never longer than the long anchor.
  const anchorKm = d2 ? d2.plannedKm : long ? long.plannedKm : null;
  if (anchorKm != null) {
    for (const s of easies) {
      if (s.plannedKm > anchorKm) { excess += s.plannedKm - anchorKm; s.plannedKm = round1(anchorKm); }
    }
  }
  // 4. Leftover → easies still under both ceilings; anything beyond that is
  //    not prescribed (a week with too few runs to hold its volume safely).
  for (const s of easies) {
    if (excess <= 0.05) break;
    const ceil = Math.min(weeklyKm * EASY_SHARE[s.type], anchorKm != null ? anchorKm : Infinity);
    const add = Math.min(excess, Math.max(0, ceil - s.plannedKm));
    if (add > 0) { s.plannedKm = round1(s.plannedKm + add); excess -= add; }
  }
}

function distributeSessions(sessionTypes, weeklyKm, weeklyElevation, targetPaceMinPerKm, terrain, raceDistanceKm = 0, thresholdPaceSecOverride = null, longTargetKm = null) {
  const n = sessionTypes.length;
  // The long run follows its own curve (computeLongRunTargets) when the
  // caller supplies one; direct callers without it keep the legacy share.
  const longCurveKm = Number(longTargetKm) > 0 ? Number(longTargetKm) : null;
  const longCeilingKm = raceDistanceKm > 0 ? longRunCapKm(raceDistanceKm) : Infinity;

  // Weight map for km distribution
  const kmWeights = {
    'easy': 0.20,
    'tempo': 0.20,
    'intervals': 0.20,
    'long': 0.45,
    'b2b-day1': 0,   // computed from B2B total
    'b2b-day2': 0,   // computed from B2B total
    'time-on-feet': 0, // duration-based, not km
    'vertical': 0.15,
    'hike': 0, // duration-based
    'recovery-easy': 1.0 / n,
  };

  // Check for B2B sessions
  const hasB2B = sessionTypes.includes('b2b-day1');
  const hasTimeFeet = sessionTypes.includes('time-on-feet');
  const hasHike = sessionTypes.includes('hike');

  // Calculate fixed allocations first
  let remainingKm = weeklyKm;
  const sessions = sessionTypes.map(type => ({
    type,
    plannedKm: null,
    plannedDuration: null,
    plannedElevation: null,
  }));

  // Time-on-feet: allocate by duration (2.5–4 hours), estimate km roughly
  if (hasTimeFeet) {
    const tofIdx = sessionTypes.indexOf('time-on-feet');
    const durationMin = clamp(weeklyKm * 2.5, 120, 300); // 2–5 hours
    sessions[tofIdx].plannedDuration = Math.round(durationMin);
    sessions[tofIdx].plannedKm = null; // km irrelevant for time-on-feet
  }

  // Hike: 60–120 min duration
  if (hasHike) {
    const hikeIdx = sessionTypes.indexOf('hike');
    sessions[hikeIdx].plannedDuration = 90; // 90 min default
    sessions[hikeIdx].plannedKm = null;
  }

  // B2B split per §Pillar 2: "Day 1 = 60–70% of Day 2 distance. Day 2 = the
  // long run." So day-1 is SHORTER. Using the middle of the range (65%):
  //   d1 = 0.65 × d2  AND  d1 + d2 = b2bTotal
  //   → d2 = b2bTotal / 1.65 ≈ 60.6%,  d1 ≈ 39.4%
  // We round to 40/60 for readability.
  if (hasB2B) {
    // Day 2 IS the long run, so it follows the curve; the pair is sized from
    // it. Without a curve the pair keeps the legacy 45%-of-week share.
    const d2Target = longCurveKm != null
      ? Math.min(longCurveKm, longCeilingKm, weeklyKm * 0.45)
      : (weeklyKm * 0.45) * 0.60;
    const b2bTotal = d2Target / 0.60;
    const d1Idx = sessionTypes.indexOf('b2b-day1');
    const d2Idx = sessionTypes.indexOf('b2b-day2');
    sessions[d1Idx].plannedKm = Math.round(b2bTotal * 0.40 * 10) / 10;
    sessions[d2Idx].plannedKm = Math.round(b2bTotal * 0.60 * 10) / 10;
    remainingKm -= b2bTotal;
  }

  // Long run on the curve: fixed before the share-out, like the B2B pair.
  if (longCurveKm != null && sessionTypes.includes('long')) {
    const longIdx = sessionTypes.indexOf('long');
    const km = Math.min(longCurveKm, longCeilingKm, weeklyKm * 0.55);
    sessions[longIdx].plannedKm = Math.round(km * 10) / 10;
    remainingKm -= km;
  }

  // Distribute remaining km to non-B2B, non-duration sessions
  const kmSessions = sessions.filter(s =>
    s.plannedKm === null && s.plannedDuration === null
  );

  if (kmSessions.length > 0) {
    // Normalize weights for remaining sessions, then CAP each session at a
    // sensible maximum share of weeklyKm. Without the cap, a 4-run week with
    // B2B on + Hike on ends up with Tempo being the only non-duration,
    // non-B2B km-session; the normalization then dumps ~55% of weeklyKm onto
    // Tempo (35+ km Tempo sessions are nonsense). The cap keeps session
    // allocations proportional to the base-weight intent even when other
    // sessions are absorbed by modifiers.
    const sessionCap = {
      'easy': 0.28,          // up to 28% of weekly on a single easy run
      'tempo': 0.25,         // intensity cap — never more than 25% of weekly
      'intervals': 0.22,
      'long': 0.55,          // long can eat up to 55% of a solo-km week
      'vertical': 0.22,
      'recovery-easy': 0.45, // recovery weeks where it's mostly recovery-easy
    };
    const totalWeight = kmSessions.reduce((sum, s) => sum + (kmWeights[s.type] || 0.2), 0);
    for (const s of kmSessions) {
      const w = (kmWeights[s.type] || 0.2) / totalWeight;
      const uncapped = remainingKm * w;
      const capFrac = sessionCap[s.type] ?? 0.35;
      const cap = weeklyKm * capFrac;
      s.plannedKm = Math.round(Math.min(uncapped, cap) * 10) / 10;
    }
  }

  // §Pillar 4 — Long-run cap: single long run never exceeds 75% of race
  // distance. Apply AFTER weekly-weight distribution so the cap is the final
  // word. For 100K (raceDistanceKm = 100), that's 75km max. For 50K → 37.5km.
  if (raceDistanceKm > 0) {
    // 75% of race distance for ultras/marathon; sub-half goals need a long
    // run LONGER than the race (5K/10K: 1.5× distance, ≥ 12 km), the half
    // 90% of distance (§"Short-distance goals").
    const longCap = longRunCapKm(raceDistanceKm);
    for (const s of sessions) {
      if (s.type === 'long' && s.plannedKm != null && s.plannedKm > longCap) {
        s.plannedKm = Math.round(longCap * 10) / 10;
      }
    }
  }

  // Resolve the threshold anchor once — used by both the tempo-duration cap
  // and the per-session duration loop. Prefer the value passed by
  // generatePlan (observed Strava pace when present, Riegel otherwise — see
  // resolveThresholdPace); fall back to a local Riegel projection for direct
  // callers (tests / legacy).
  const thresholdPaceSec = (() => {
    const ov = Number(thresholdPaceSecOverride);
    if (ov > 0) return ov;
    const tf = targetPaceMinPerKm * 60 * raceDistanceKm;
    return thresholdPaceSecPerKm(raceDistanceKm, tf);
  })();

  // §"Session types" — Tempo / Threshold is "20–40 min of quality work", NOT
  // a volume bucket. The percentage caps above still let tempo balloon on a
  // big week: 25% of a 115 km peak ≈ 29 km, i.e. a ~2-hour "tempo" — nonsense
  // (this is the "22 km tempo at 4:01" the founder flagged). Cap the tempo
  // session to a realistic duration at threshold pace and pour the freed km
  // into the week's easy runs (the 80%, §Pillar 1) so the weekly total still
  // lands. A tempo session is the ONLY session sized by duration rather than
  // by volume share — quality is time-bounded by physiology, not by the week.
  if (thresholdPaceSec != null && thresholdPaceSec > 0) {
    const TEMPO_MAX_MIN = 40;
    const maxTempoKm = Math.round(((TEMPO_MAX_MIN * 60) / thresholdPaceSec) * 10) / 10;
    const tempoS = sessions.find((s) => s.type === 'tempo');
    if (tempoS && tempoS.plannedKm != null && tempoS.plannedKm > maxTempoKm) {
      const overflow = tempoS.plannedKm - maxTempoKm;
      tempoS.plannedKm = maxTempoKm;
      const easies = sessions.filter(
        (s) => (s.type === 'easy' || s.type === 'recovery-easy') && s.plannedKm != null
      );
      if (easies.length) {
        const add = overflow / easies.length;
        for (const s of easies) s.plannedKm = Math.round((s.plannedKm + add) * 10) / 10;
      }
    }
  }

  // §Pillar 1 / §Pillar 2 — an easy run is never the week's big run. The
  // tempo overflow above bypassed the per-session caps: in a 4-run B2B week
  // the single easy run absorbed the whole leftover (a 51 km "easy" against
  // a 31 km B2B day-2). Re-apply the easy cap (and never exceed the long
  // anchor), then move the excess onto the long anchor — the B2B pair keeps
  // its 40/60 split (§Pillar 2 day-1 ≈ 65 % of day-2), a plain long run its
  // share cap — never past the long-run cap (§Pillar 4). Weekly volume is
  // preserved; dropping it here would put taper week 1 above the reduced
  // peak week (§Anti-pattern #6).
  capEasyOverflow(sessions, weeklyKm, raceDistanceKm, longCurveKm != null);

  // Elevation distribution
  if (weeklyElevation > 0) {
    const verticalSession = sessions.find(s => s.type === 'vertical');
    if (verticalSession) {
      verticalSession.plannedElevation = Math.round(weeklyElevation * 0.5);
      // Spread remaining across long/b2b
      const others = sessions.filter(s =>
        s.type === 'long' || s.type === 'b2b-day1' || s.type === 'time-on-feet'
      );
      const remainElev = weeklyElevation * 0.5;
      for (const s of others) {
        s.plannedElevation = Math.round(remainElev / (others.length || 1));
      }
    } else {
      // No vertical session — spread across long/b2b
      const elevSessions = sessions.filter(s =>
        ['long', 'b2b-day1', 'b2b-day2', 'time-on-feet'].includes(s.type)
      );
      if (elevSessions.length > 0) {
        const perSession = Math.round(weeklyElevation / elevSessions.length);
        for (const s of elevSessions) {
          s.plannedElevation = perSession;
        }
      }
    }
  }

  // Per-session duration for km-based sessions. Duration-based types
  // (time-on-feet, hike) already had plannedDuration set earlier and are
  // skipped here.
  //
  // THRESHOLD-ANCHORED PACING MODEL
  // Every session pace is (threshold_pace × type_multiplier). Threshold is
  // derived from goal time via Riegel 1.30 projection to half-marathon.
  // NO separate terrain multiplier — goal time already encodes terrain.
  //
  //   - 'tempo'          → threshold × 1.00
  //   - 'b2b-day1'       → threshold × 1.25
  //   - 'long'           → threshold × 1.20
  //   - 'easy'           → threshold × 1.35
  //   - 'b2b-day2'       → threshold × 1.40
  //   - 'recovery-easy'  → threshold × 1.45
  //   - 'vertical'       → no estimate (hill terrain too variable)
  //
  // Methodology: /docs/training_methodology.md §Pillar 1.
  //
  // NOTE: the client helper (src/App.js → derivePlannedDurationMin) will
  // OVERRIDE these baked-in durations at render time if the user has
  // observed Strava data. The server values are the cold-start fallback.
  // `thresholdPaceSec` was resolved once above (before the tempo cap).
  for (const s of sessions) {
    if (s.plannedDuration != null) continue;              // already set (TOF, hike)
    if (s.plannedKm == null || s.plannedKm <= 0) continue; // nothing to compute
    if (s.type === 'vertical') continue;                   // no pace prescription
    const mult = SESSION_THRESHOLD_MULTIPLIER[s.type];
    if (mult == null) continue;                            // unknown type — skip
    if (thresholdPaceSec == null) continue;                // no anchor
    const paceSec = thresholdPaceSec * mult;
    // min = km × pace_sec_per_km / 60
    s.plannedDuration = Math.round((s.plannedKm * paceSec) / 60);
  }

  return sessions;
}

const WEEKDAY_MAP = {
  'Monday': 0, 'Tuesday': 1, 'Wednesday': 2, 'Thursday': 3,
  'Friday': 4, 'Saturday': 5, 'Sunday': 6,
};

/**
 * Day placement + placement-dependent modifiers.
 *
 * Takes the TYPE LIST from `applyPhaseTypeModifiers` (pre-modifier, pre-
 * distribution), returns `[{ type, slot }]` with FINAL types after B2B and
 * Hike modifiers, where `slot` is the index into `sortedDays`.
 *
 * This runs BEFORE `distributeSessions` so km/duration allocations use the
 * post-modifier types. Critical: an 'easy' that gets flipped to 'hike' is
 * fed to `distributeSessions` as 'hike', so it gets a 90-min duration and
 * `plannedKm = null` — not a km value from the easy allocation.
 *
 * Rules (in order):
 *   1. Long anchor ('long' / 'time-on-feet') → LATEST available day.
 *   2. If B2B is on AND phase is build/peak AND there's a consecutive day
 *      pair AND an easy slot exists: convert long → b2b-day1 on earlier
 *      pair-day, convert one easy → b2b-day2 on later pair-day.
 *   3. Tempo/vertical: slot with MAX gap from long anchor, preferring
 *      buffer ≥ 2 days. The "post-anchor slot" — the run immediately
 *      following the anchor in chronological order, including cross-week
 *      wrap to slot 0 — is EXCLUDED from tempo placement. Per methodology
 *      §Anti-pattern #2, tempo must never sit within 48h of a long run.
 *      The cross-week wrap case is the most common violation: a Sunday
 *      long with Monday in availableDays would otherwise become "Sun long
 *      → Mon tempo" each week. If tempo cannot fit anywhere except the
 *      post-anchor slot, it is DEMOTED to 'easy' so the long → easy rule
 *      still holds (rare; only happens in 3-run B2B configurations where
 *      no third slot has separation from the b2b-day2 anchor).
 *   4. Easy / recovery-easy fill remaining slots in calendar order.
 *   5. Hike modifier: ONE easy (or recovery-easy in recovery weeks) →
 *      'hike', on the LATEST eligible non-anchor day.
 *
 * A weekly sanity check warns (doesn't throw) if all final types are identical.
 */
function planPlacements(types, sortedDays, phase, {
  includeBackToBack = false,
  includeHikes = false,
  elevationGain = 0,
  terrain = 'road',
  isRecoveryWeek = false,
  longRunDay = null,
  tempoDay = null,
} = {}) {
  const offsets = sortedDays.map((d) => WEEKDAY_MAP[d] ?? 0);
  if (sortedDays.length === 0) return [];

  // Preferred long-run day (§Adaptation/UX): the runner may pin the long run
  // to a specific weekday. Resolve it to a slot index if that day is actually
  // in availableDays; otherwise fall back to the default "latest day" rule.
  // The buffer/B2B logic below keys off this index, so all 48h and
  // consecutive-pair guarantees still hold — we only move the ANCHOR.
  const preferredLongSlot = (longRunDay && sortedDays.includes(longRunDay))
    ? sortedDays.indexOf(longRunDay)
    : -1;

  let workingTypes = [...types];
  if (workingTypes.length > sortedDays.length) {
    console.warn(`[planGenerator] planPlacements: ${workingTypes.length} types but only ${sortedDays.length} available days — dropping surplus`);
    workingTypes = workingTypes.slice(0, sortedDays.length);
  }

  const placements = workingTypes.map((t) => ({ type: t, slot: -1 }));
  const used = new Array(sortedDays.length).fill(false);

  // ── Step 1+2: Long anchor (with optional B2B pairing) ──
  const longIdx = placements.findIndex((p) => LONG_ANCHOR_TYPES.has(p.type));
  let anchorSlot = -1; // the "long fatigue" slot — used as tempo-buffer reference
  let b2bApplied = false;

  if (longIdx >= 0 && includeBackToBack && !isRecoveryWeek && (phase === 'build' || phase === 'specific') && sortedDays.length >= 2) {
    // Find the consecutive pair to use for B2B. If the runner pinned a long
    // day AND the day before it is also available (consecutive), prefer that
    // pair so the long run lands on the chosen day. Otherwise use the latest
    // consecutive pair (default).
    let latestPair = null;
    for (let i = 0; i < sortedDays.length - 1; i++) {
      if (offsets[i + 1] - offsets[i] === 1) latestPair = [i, i + 1]; // keep latest
    }
    if (preferredLongSlot > 0 && offsets[preferredLongSlot] - offsets[preferredLongSlot - 1] === 1) {
      latestPair = [preferredLongSlot - 1, preferredLongSlot];
    }
    if (latestPair) {
      const easyIdx = placements.findIndex((p) => EASY_TYPES.has(p.type) && p.slot === -1);
      if (easyIdx >= 0) {
        placements[longIdx].type = 'b2b-day1';
        placements[longIdx].slot = latestPair[0];
        placements[easyIdx].type = 'b2b-day2';
        placements[easyIdx].slot = latestPair[1];
        used[latestPair[0]] = true;
        used[latestPair[1]] = true;
        anchorSlot = latestPair[1];
        b2bApplied = true;
      }
    }
  }
  if (!b2bApplied && longIdx >= 0) {
    // Plain placement: prefer the pinned long-run day if free, else the
    // latest unused day.
    let s = -1;
    if (preferredLongSlot >= 0 && !used[preferredLongSlot]) {
      s = preferredLongSlot;
    } else {
      s = sortedDays.length - 1;
      while (s >= 0 && used[s]) s--;
    }
    if (s >= 0) {
      placements[longIdx].slot = s;
      used[s] = true;
      anchorSlot = s;
    }
  }

  // ── Step 3: Tempo / vertical with buffer ≥ 2 days from anchor ──
  //
  // Methodology §Anti-pattern #2: tempo work must never sit within 48h of a
  // long run. The "post-anchor slot" is the run that comes RIGHT AFTER the
  // long anchor in chronological order — that's either the next unused slot
  // later in the same week, or (when the anchor is on the last available
  // day) slot 0 of the next week, which is the same day-of-week as slot 0
  // of THIS week's sortedDays. We exclude that slot from tempo placement so
  // the easy fill in step 4 lands there instead. Without this, a Sunday
  // long + Monday-in-availableDays produced "Sun long → Mon tempo" every
  // week — the bug this rule fixes.
  const postAnchorSlot = (() => {
    if (anchorSlot < 0) return -1;
    for (let i = anchorSlot + 1; i < sortedDays.length; i++) {
      if (!used[i]) return i;
    }
    return 0; // wrap to next week's first slot
  })();

  const tempoIdx = placements.findIndex((p) => TEMPO_TYPES.has(p.type) && p.slot === -1);
  if (tempoIdx >= 0) {
    const anchorOffset = anchorSlot >= 0 ? offsets[anchorSlot] : null;
    // §Anti-pattern #2 — "including across week boundaries": the plan repeats
    // weekly, so the distance between two weekdays is CYCLIC. A Sunday tempo
    // before a Monday long run is 1 day, not 6. min(|a−b|, 7−|a−b|) covers
    // both directions (before and after the anchor).
    const dayGap = (i) => {
      if (anchorOffset == null) return 99;
      const d = Math.abs(offsets[i] - anchorOffset);
      return Math.min(d, 7 - d);
    };
    let pickSlot = -1;
    let pickBuf = -1;
    // Soft preference: honor the user's chosen tempo/quality day only when it
    // still keeps the ≥2-day buffer to the long anchor and isn't the
    // post-anchor slot. Otherwise fall through to the normal max-buffer rule.
    const prefTempoSlot = (tempoDay && sortedDays.includes(tempoDay)) ? sortedDays.indexOf(tempoDay) : -1;
    if (prefTempoSlot >= 0 && !used[prefTempoSlot] && prefTempoSlot !== postAnchorSlot) {
      const b = dayGap(prefTempoSlot);
      if (b >= 2) { pickSlot = prefTempoSlot; pickBuf = b; }
    }
    // Prefer buffer ≥ 2; within that, take MAX buffer. Skip post-anchor slot.
    if (pickSlot < 0) for (let i = 0; i < sortedDays.length; i++) {
      if (used[i]) continue;
      if (i === postAnchorSlot) continue;
      const buf = dayGap(i);
      if (buf >= 2 && buf > pickBuf) { pickBuf = buf; pickSlot = i; }
    }
    // No slot keeps 48 h from the long anchor (very tight weeks). The old
    // fallback placed tempo anyway at the max buffer (< 2 days) — a direct
    // §Anti-pattern #2 violation. Demote to easy instead (below).
    if (pickSlot >= 0) {
      placements[tempoIdx].slot = pickSlot;
      used[pickSlot] = true;
    } else {
      // No slot ≥ 48 h from the anchor (e.g. 3-run B2B week where
      // b2b-day1/day2 consume two of three slots, or a Sun/Mon/Tue cluster
      // around a pinned long day). Demote tempo → easy, per §Anti-pattern #2.
      placements[tempoIdx].type = 'easy';
      // Falls through to easy-fill step 4.
    }
  }

  // ── Step 4: Easy / recovery-easy fill remaining slots in calendar order ──
  const remainingSlots = [];
  for (let i = 0; i < sortedDays.length; i++) if (!used[i]) remainingSlots.push(i);
  let k = 0;
  for (let i = 0; i < placements.length; i++) {
    if (placements[i].slot !== -1) continue;
    if (k >= remainingSlots.length) continue; // no room — safety
    placements[i].slot = remainingSlots[k++];
    used[placements[i].slot] = true;
  }

  // ── Step 5: Hike modifier — ONE easy → hike on latest eligible day ──
  //
  // Gate per `/docs/training_methodology.md` §Pillar 4 + §Anti-pattern #4:
  //   Hiking only allowed when
  //     (a) goal has significant elevation (elevationGain ≥ 1500m) OR
  //         terrain is explicitly 'mountain', AND
  //     (b) week is in 'specific' phase (or the recovery week of a specific
  //         block — underlying === 'specific').
  //
  // If `includeHikes` is on but these conditions are not met, the toggle is
  // silently ignored. This prevents the April-19 bug where Hiking bled into
  // Base and Build phases of flat/road ultras.
  //
  // NOTE: the doc also allows hiking when "B2B day 2 is very long and impact
  // reduction is needed" — that's deferred until the generator has km info
  // at placement time (currently distribution happens after placement).
  const hasSignificantElevation = Number(elevationGain) >= 1500 || terrain === 'mountain';
  const isSpecificPhase = phase === 'specific';
  // Hikes never fire on recovery weeks — keep the recovery week genuinely easy.
  const hikingEligible = includeHikes && hasSignificantElevation && isSpecificPhase && !isRecoveryWeek;

  if (includeHikes && !hikingEligible) {
    // Surface the mismatch so downstream bug reports can trace it.
    if (!hasSignificantElevation) {
      console.warn(`[planGenerator] hiking toggle ignored — elevationGain=${elevationGain}m < 1500m and terrain="${terrain}" (not mountain). See /docs/training_methodology.md §Pillar 4.`);
    } else if (!isSpecificPhase) {
      // This is a normal case (base/build weeks), log at debug-level intent.
      // (Left as warn for now so the pattern is visible during the refactor window.)
      console.warn(`[planGenerator] hiking toggle skipped for phase="${phase}" — hikes only allowed in 'specific' phase. See /docs/training_methodology.md §Pillar 4.`);
    }
  }

  if (hikingEligible) {
    let targetIdx = -1;
    let targetSlot = -1;
    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      if (!EASY_TYPES.has(p.type) || p.slot < 0) continue;
      if (p.slot > targetSlot) { targetSlot = p.slot; targetIdx = i; }
    }
    if (targetIdx >= 0) placements[targetIdx].type = 'hike';
  }

  // ── Sanity check: all-same-type is a bug indicator ──
  // Exempt: recovery weeks with ≤ 2 sessions can legitimately be all
  // recovery-easy (rare edge case where no long anchor fits).
  const placed = placements.filter((p) => p.slot >= 0);
  const uniqueTypes = new Set(placed.map((p) => p.type));
  if (uniqueTypes.size === 1 && placed.length > 2) {
    console.warn(`[planGenerator] WARNING: all ${placed.length} sessions in ${phase} week have type "${[...uniqueTypes][0]}". Likely a modifier-application bug.`);
  }

  return placements;
}

/**
 * Attach dates and HR/purpose metadata to distributed sessions using placements.
 * Pure glue — no logic, just zip + stamp.
 *
 * @param {Array} placements — [{ type, slot }]  (output of planPlacements)
 * @param {Array} distributed — [{ type, plannedKm, plannedDuration, plannedElevation }] (output of distributeSessions, same length and type-order as placements)
 */
function attachDates(placements, distributed, weekStartDate, sortedDays) {
  const offsets = sortedDays.map((d) => WEEKDAY_MAP[d] ?? 0);
  const out = [];
  for (let i = 0; i < placements.length; i++) {
    const p = placements[i];
    if (p.slot < 0) continue;
    const dist = distributed[i] || {};
    const date = addDays(weekStartDate, offsets[p.slot]);
    out.push({
      type: p.type,
      date: toLocalDateString(date),
      plannedKm: dist.plannedKm ?? null,
      plannedDuration: dist.plannedDuration ?? null,
      plannedElevation: dist.plannedElevation ?? null,
      hrZone: SESSION_HR_ZONES[p.type] || 'Z2',
      purpose: SESSION_PURPOSES[p.type] || '',
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * §Anti-pattern #2, whole-plan pass: no tempo/quality session within 48 h of
 * a long anchor, on REAL dates. planPlacements enforces the buffer inside a
 * week (cyclically), but the anchor can move between weeks — e.g. a Sat/Sun
 * B2B pair in Specific followed by a taper week whose long run honours a
 * pinned Thursday puts a Monday tempo 24 h after Sunday's day-2. A quality
 * session that lands inside that window is demoted to easy (the run after a
 * long effort is recovery). Mutates `weeks`.
 */
const BUFFER_ANCHOR_TYPES = new Set(['long', 'b2b-day2', 'time-on-feet']);
function enforceQualityBuffer(weeks, thresholdPaceSec) {
  const dayNum = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / DAY_MS;
  const anchors = [];
  for (const w of weeks) for (const s of w.sessions) {
    if (BUFFER_ANCHOR_TYPES.has(s.type) && s.date) anchors.push(dayNum(s.date));
  }
  for (const w of weeks) for (const s of w.sessions) {
    if (!TEMPO_TYPES.has(s.type) || !s.date) continue;
    const d = dayNum(s.date);
    if (!anchors.some((a) => Math.abs(a - d) < 2)) continue;
    s.type = 'easy';
    s.hrZone = SESSION_HR_ZONES.easy || 'Z2';
    s.purpose = SESSION_PURPOSES.easy || '';
    if (s.plannedKm != null && s.plannedKm > 0 && Number(thresholdPaceSec) > 0) {
      s.plannedDuration = Math.round((s.plannedKm * thresholdPaceSec * SESSION_THRESHOLD_MULTIPLIER.easy) / 60);
    }
  }
}

/**
 * Refusal for "there is no volume to build a plan from" (§"Readiness gate").
 * Carries the same options object as any other readiness refusal, plus the
 * base-building note the UI shows.
 */
function blockedForNoBase(readiness, mode) {
  const verdict = Object.assign({}, readiness, {
    ready: false,
    noBaseVolume: true,
    baseNote:
      'Your recent history shows no weekly running volume to build from. ' +
      'Run easy for three to four weeks, then generate the plan — there is ' +
      'no safe ramp that starts from nothing.',
  });
  return {
    blocked: true,
    reason: 'readiness',
    planFormatVersion: PLAN_FORMAT_VERSION,
    goalMode: mode,
    readiness: verdict,
  };
}

/* ─── main generator ──────────────────────────────────────── */

/**
 * Canonical plan-format version. Bump whenever the plan SHAPE or generator
 * SEMANTICS change so the client (PLAN_FORMAT_VERSION in src/App.js) can flag
 * stale stored plans and force regeneration.
 *
 * Version history:
 *   1 — legacy (missing field entirely), old sessionTypesForWeek +
 *       toISOString date bug. DO NOT TRUST dates in v1 plans.
 *   2 — 5-pass pipeline, local-date serialization, recovery×0.65.
 *   3 — per-session plannedDuration via race-pace anchor (wrong for ultras).
 *   4 — threshold-anchored pacing (Riegel-1.30 from goal time).
 *   5 — Recovery no longer a top-level phase; isRecoveryWeek flag.
 *   6 — methodology-anchored volume math; backsolved start; taper from
 *       ACTUAL achieved peak (fixes anti-pattern #6).
 *   7 — §Anti-pattern #2 enforced strictly: post-anchor slot excluded from
 *       tempo placement (cross-week wrap fix).
 *   8 — current: userBaseline-driven generation. Structured baseline
 *       (median weekly km / consistency / longest run / observed threshold
 *       pace) replaces the naive currentWeeklyKm scalar. Adds the readiness
 *       gate (refuses an unsafe ramp, returns 3 honest options), the pace
 *       sanity guard (refuses a goal-derived pace > 15% faster than observed
 *       runs — the anti-Garmin rule), server-side observed-threshold pace
 *       (saved plan durations now reflect real fitness, not just the client
 *       render-time override), and the finish-only goal mode (a safe forward
 *       ramp from the real baseline for runners who can't reach the goal).
 *       New plan fields: goalMode, paceModel, readiness, userBaseline,
 *       finishNote?, acknowledgedStretch?. generatePlan may now return a
 *       BLOCKED result: { blocked:true, reason:'readiness'|'pace-guard', … }.
 *   9 — GAP-rTSS / stress-test tier 1 (matcher two-pass, adherence window).
 *  10 — multi-goal macro plan (macroPlan.js): plans carry `goals`, `macro`,
 *       `primaryGoal`; embedded `race` sessions with goalId/priority; new
 *       `intervals` session type (speed block); per-week `raceWeek` marker;
 *       accepted adjustments add `changeLog` / `adjustedAt` / `originalPlannedKm`.
 *  11 — soft `tempoDay` preference (methodology §"Preferred session days").
 *  12 — sense-check 2026-09-19 fixes: a measured 0 km/wk baseline is gated
 *       (§"Readiness gate"); tempo buffer is cyclic in both directions and
 *       tempo is demoted rather than placed < 48 h from the long anchor
 *       (§Anti-pattern #2); easy runs are capped and never exceed the long
 *       anchor, overflow moves to the B2B pair / long run (§Pillar 1/2);
 *       half/marathon long-run caps use the correct brackets
 *       (§"Short-distance goals"). changeLog may carry `readinessOverride`
 *       entries (worker).
 *       Approved decisions (2026-09-20, same version — v12 was never
 *       deployed): recovery weeks are −25% and Base/Build only (§Pillar 3);
 *       the long run follows its own ascending curve peaking mid-Specific
 *       with a ~40%-of-peak taper (computeLongRunTargets) instead of a flat
 *       45% share; a plan is never emitted with zero volume (no base, or a
 *       timeline that is all taper → readiness refusal with `noBaseVolume`);
 *       week.plannedKm is now the km the sessions PRESCRIBE, with the ramp
 *       target kept alongside as `rampTargetKm`.
 *  13 — duration-only sessions carry `estimatedKm`, and week.plannedKm counts
 *       it (§Pillar 4 "two volume numbers"). time-on-feet and hike prescribe
 *       time, so they held `plannedKm: null` and contributed nothing to the
 *       week. At 3 sessions/week — the app default — that understated the
 *       four Specific weeks by ~49% (34 km reported against ~66 km
 *       prescribed): the chart drew a cliff at the peak of the plan, and an
 *       athlete who ran exactly the prescription was flagged at 194% of
 *       target, "Over target. Watch for fatigue signals." The estimate is
 *       derived from the plan's own threshold pace at a multiplier at or
 *       below the slowest pace the plan prescribes, so it never inflates a
 *       week and never flatters the readiness gate or the ramp. Session
 *       `plannedKm` stays null — these are still prescribed as time.
 *       weeklyLoad.js counts it too (§Pillar 5 rule 8, one definition).
 */
const PLAN_FORMAT_VERSION = 13;

/**
 * Resolve the threshold pace that anchors every prescribed training pace.
 *
 * Precedence (§Pillar 1 + §"User baseline"):
 *   1. OBSERVED — the runner's fastest recent 3–15 km pace from Strava.
 *      A real, measured number; always preferred when present.
 *   2. RIEGEL  — projected from the entered goal time to half-marathon
 *      distance with exponent 1.30. A cold-start estimate only.
 *
 * Returns { paceSec, source: 'observed' | 'riegel' | null }.
 */
function resolveThresholdPace({ raceDistanceKm, targetFinishSec, observedThresholdPaceSec }) {
  const observed = Number(observedThresholdPaceSec);
  if (Number.isFinite(observed) && observed > 0) {
    return { paceSec: observed, source: 'observed' };
  }
  const riegel = thresholdPaceSecPerKm(raceDistanceKm, targetFinishSec);
  return (riegel != null && riegel > 0)
    ? { paceSec: riegel, source: 'riegel' }
    : { paceSec: null, source: null };
}

/**
 * Target finish → seconds, or null for "no time goal". Numeric seconds win.
 * Strings: "H:MM:SS", or a 2-part "M:SS" (minutes:seconds — the format the
 * goal wizard and the goals API use for sub-hour targets; reading it as
 * H:MM turned a 45:00 10K target into 45 hours).
 */
function parseTargetFinishSec(targetFinish, targetFinishSec) {
  const n = Number(targetFinishSec);
  if (targetFinishSec != null && targetFinishSec !== '' && Number.isFinite(n) && n > 0) return Math.round(n);
  if (typeof targetFinish !== 'string' || !targetFinish.trim()) return null;
  const parts = targetFinish.trim().split(':').map(Number);
  if (!parts.every((x) => Number.isFinite(x) && x >= 0)) return null;
  let sec = null;
  if (parts.length === 3) sec = parts[0] * 3600 + parts[1] * 60 + parts[2];
  else if (parts.length === 2) sec = parts[0] * 60 + parts[1];
  return sec != null && sec > 0 ? sec : null;
}

/** Seconds → "H:MM:SS" (always three parts — unambiguous on re-parse). */
function formatHms(sec) {
  const t = Math.round(sec);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const x = t % 60;
  return h + ':' + String(m).padStart(2, '0') + ':' + String(x).padStart(2, '0');
}

/**
 * Generate a complete ultra training plan.
 *
 * Canonical reference: `/docs/training_methodology.md`. Every non-trivial
 * rule in this function cites a section of that doc.
 *
 * @param {Object}  params
 * @param {number}  params.raceDistance        km
 * @param {string}  params.raceDate            ISO date string
 * @param {string}  params.targetFinish        "H:MM:SS" (a 2-part "M:SS" is
 *                                             minutes:seconds). Omit / null =
 *                                             no time goal.
 * @param {number}  params.targetFinishSec     seconds; wins over targetFinish.
 * @param {string}  params.terrain             trail | mixed | road | mountain
 * @param {number}  params.elevationGain       total race elevation (m); gates
 *                                             hiking per §Pillar 4
 * @param {number}  params.sessionsPerWeek     3 | 4 | 5
 * @param {string[]} params.availableDays
 * @param {boolean} params.includeBackToBack   default from raceDistance (§Pillar 2)
 * @param {boolean} params.includeHikes        gated server-side per §Pillar 4
 * @param {boolean} params.includeVertical
 * @param {Object}  params.userBaseline        structured baseline from
 *                                             userBaseline.computeUserBaseline.
 *                                             Canonical volume + pace input.
 * @param {number}  params.observedThresholdPaceSec  explicit override (sec/km);
 *                                             userBaseline takes precedence.
 * @param {number}  params.currentWeeklyKm     LEGACY scalar fallback when no
 *                                             userBaseline is supplied.
 * @param {string}  params.goalMode            'standard' | 'finish-only'
 * @param {boolean} params.acknowledgeReadiness user explicitly accepted a
 *                                             stretch plan past the gate.
 * @param {number}  params.now                 epoch ms — injected for testing.
 *
 * @returns {Object} a plan object, OR a blocked result
 *                   { blocked:true, reason:'readiness'|'pace-guard', … }.
 */
function generatePlan({
  raceDistance,
  raceDate,
  targetFinish,
  targetFinishSec: targetFinishSecParam = null,
  terrain = 'trail',
  elevationGain = 0,
  sessionsPerWeek = 3,
  availableDays = ['Tuesday', 'Thursday', 'Saturday'],
  longRunDay = null, // preferred weekday for the long run (name) or null = auto
  tempoDay = null, // preferred weekday for the tempo/quality session (soft)
  includeBackToBack, // defaulted from raceDistance below
  includeHikes = false,
  includeVertical = false,
  currentWeeklyKm = 30,
  userBaseline = null,
  observedThresholdPaceSec = null,
  goalMode = 'standard',
  acknowledgeReadiness = false,
  now = null,
} = {}) {
  const mode = goalMode === 'finish-only' ? 'finish-only' : 'standard';

  // §Pillar 2 — B2B default-on for ultra distances ≥ 50K. Explicit `false`
  // still wins (user override); `undefined` triggers the distance default.
  if (includeBackToBack == null) {
    includeBackToBack = Number(raceDistance) >= 50;
  }

  // ── Baseline resolution (§"User baseline") ──
  // The structured userBaseline is canonical. currentWeeklyKm is the legacy
  // scalar fallback for callers that have not migrated. Observed threshold
  // pace + median run pace are read from the baseline when present.
  const baselineKnown = !!(userBaseline && Number.isFinite(userBaseline.medianWeeklyKm));
  const baselineWeeklyKm = baselineKnown
    ? userBaseline.medianWeeklyKm
    : (Number(currentWeeklyKm) || 0);
  const observedPaceSec =
    userBaseline && Number.isFinite(userBaseline.observedThresholdPaceSec)
      ? userBaseline.observedThresholdPaceSec
      : (Number.isFinite(Number(observedThresholdPaceSec)) && Number(observedThresholdPaceSec) > 0
          ? Number(observedThresholdPaceSec)
          : null);
  const medianRunPaceSec =
    userBaseline && Number.isFinite(userBaseline.medianRunPaceSec)
      ? userBaseline.medianRunPaceSec
      : null;

  // Parse the timeline.
  const raceDateObj = new Date(raceDate);
  const nowDate = now != null ? new Date(now) : new Date();
  const planStart = startOfWeek(nowDate);
  const planEnd = startOfWeek(raceDateObj);
  const totalWeeks = Math.max(1, Math.round((+planEnd - +planStart) / (7 * DAY_MS)));

  // ── Readiness gate (§"Readiness gate") ──
  // A standard plan whose ramp cannot be safely reached from the runner's
  // baseline is REFUSED — generation returns a blocked result carrying the
  // three honest options. acknowledgeReadiness lets the runner consciously
  // override (the brief forbids SILENT injury-bait plans, not informed
  // choice). finish-only plans bypass the gate by design — they ARE the
  // safe alternative. A plan with no baseline at all can't be gated, so it
  // proceeds on the legacy cold-start path. A MEASURED baseline of 0 km/wk
  // (userBaseline present, no recent running) is gated like any other —
  // backsolving a 100K ramp from zero was the exact injury-bait case.
  const readiness = assessReadiness({
    baselineWeeklyKm,
    raceDistanceKm: Number(raceDistance),
    weeksAvailable: totalWeeks,
    now: +nowDate,
    baselineKnown,
  });
  if (mode === 'standard' && readiness.hasBaseline && !readiness.ready && !acknowledgeReadiness) {
    return {
      blocked: true,
      reason: 'readiness',
      planFormatVersion: PLAN_FORMAT_VERSION,
      goalMode: mode,
      readiness,
    };
  }

  // Target finish time. No target = a "just finish" goal: NO Riegel
  // projection (there is nothing to project from), paces come from the
  // observed threshold or are not prescribed. The old '12:00:00' default
  // fabricated a 12 h goal for every distance (a 10K included).
  const targetFinishSec = parseTargetFinishSec(targetFinish, targetFinishSecParam);
  const targetPaceMinPerKm = targetFinishSec != null ? (targetFinishSec / 60) / raceDistance : null;

  // ── Threshold pace + sanity guard (§"Pace sanity guard") ──
  const threshold = resolveThresholdPace({
    raceDistanceKm: Number(raceDistance),
    targetFinishSec,
    observedThresholdPaceSec: observedPaceSec,
  });
  // A threshold pace the athlete TYPED IN (Settings) arrives through the same
  // baseline field as the Strava-observed one, flagged 'manual'. It is not a
  // pace the runner has demonstrably run, so it is guarded like a Riegel
  // projection rather than trusted like an observed pace.
  if (threshold.source === 'observed' && userBaseline && userBaseline.observedThresholdSource === 'manual') {
    threshold.source = 'manual';
  }
  // The guard fires on the Riegel cold-start path and on a manual pace. An
  // observed pace is, by definition, a pace the runner has actually run —
  // never "too fast". A Riegel projection from an over-ambitious goal time is
  // exactly what produced Garmin's 3:59/km-to-a-5:30/km-runner bug; a typed-in
  // pace can make the same mistake. If the threshold is more than 15% faster
  // than the runner's median observed run pace, the plan is refused — no
  // override.
  if (
    (threshold.source === 'riegel' || threshold.source === 'manual') &&
    threshold.paceSec != null &&
    Number.isFinite(medianRunPaceSec) && medianRunPaceSec > 0 &&
    threshold.paceSec < medianRunPaceSec * 0.85
  ) {
    return {
      blocked: true,
      reason: 'pace-guard',
      planFormatVersion: PLAN_FORMAT_VERSION,
      goalMode: mode,
      paceGuard: {
        source: threshold.source,
        prescribedThresholdPaceSec: Math.round(threshold.paceSec),
        observedMedianRunPaceSec: Math.round(medianRunPaceSec),
        ratio: Math.round((threshold.paceSec / medianRunPaceSec) * 100) / 100,
        message: threshold.source === 'manual'
          ? 'The threshold pace entered in Settings is more than 15% faster ' +
            'than the runner’s recent runs. Refusing to prescribe from it. ' +
            'Adjust it, or clear it to use the pace from Strava.'
          : 'The finish time entered implies a training pace more than 15% ' +
            'faster than the runner’s recent runs. Refusing to prescribe ' +
            'it. Enter a more realistic goal time, or sync more Strava history.',
      },
    };
  }

  // Weekly-hours estimate: goal race pace when there is a goal time, else
  // the easy-run pace off the threshold anchor; unknown when neither exists.
  const hoursPaceMinPerKm = targetPaceMinPerKm != null
    ? targetPaceMinPerKm
    : (threshold.paceSec != null ? (threshold.paceSec * SESSION_THRESHOLD_MULTIPLIER.easy) / 60 : null);

  // §"Readiness gate" — a finish-only plan ramps FORWARD from the real
  // baseline, so a measured 0 km/wk has nothing to ramp from: it produced an
  // all-zero plan. Route it through the gate with a "build a base first"
  // message instead. An acknowledged stretch cannot override this — there is
  // no volume to stretch from.
  if (mode === 'finish-only' && readiness.hasBaseline && baselineWeeklyKm <= 0) {
    return blockedForNoBase(readiness, mode);
  }

  // Compute phases and volumes.
  const phases = computePhases(totalWeeks, raceDistance);
  const longTargets = computeLongRunTargets(phases, Number(raceDistance));
  const weeklyVolumes = computeWeeklyVolumes(phases, baselineWeeklyKm, raceDistance, { goalMode: mode });

  // Never emit an empty plan. A timeline with no real training weeks (every
  // week is taper) or a baseline with nothing to ramp from produces all-zero
  // volumes — a plan that prescribes nothing is not a plan. Refuse through
  // the readiness gate instead (§"Readiness gate").
  if (!(Math.max.apply(null, weeklyVolumes.concat([0])) > 0)) {
    return blockedForNoBase(readiness, mode);
  }

  // Normalise availableDays once — sorted Mon..Sun.
  const sortedDays = [...availableDays].sort(
    (a, b) => (WEEKDAY_MAP[a] ?? 0) - (WEEKDAY_MAP[b] ?? 0)
  );

  // Build weeks.
  const weeks = [];
  for (let w = 0; w < totalWeeks; w++) {
    const weekStart = addDays(planStart, w * 7);
    const phase = phases[w];
    const plannedKm = weeklyVolumes[w];
    const plannedElevation = weeklyElevationTarget(phase.phase, terrain, phase.isRecoveryWeek);
    const plannedHoursOf = (km) => (hoursPaceMinPerKm != null ? estimateDurationHours(km, hoursPaceMinPerKm, terrain) : null);

    const baseTypes = baseSessionTypes(sessionsPerWeek, mode);
    const phaseTyped = applyPhaseTypeModifiers(baseTypes, phase.phase, {
      includeVertical,
      terrain,
      isRecoveryWeek: phase.isRecoveryWeek,
      goalMode: mode,
      shortDistance: isShortDistance(raceDistance),
    });
    const placements = planPlacements(phaseTyped, sortedDays, phase.phase, {
      includeBackToBack,
      includeHikes,
      elevationGain,
      terrain,
      isRecoveryWeek: phase.isRecoveryWeek,
      longRunDay,
      tempoDay,
    });
    const finalTypes = placements.map((p) => p.type);
    const distributed = distributeSessions(
      finalTypes, plannedKm, plannedElevation, targetPaceMinPerKm, terrain,
      raceDistance, threshold.paceSec, longTargets[w]
    );
    const sessions = attachDates(placements, distributed, weekStart, sortedDays);
    for (const s of sessions) {
      if (s.type === 'intervals') s.purpose = intervalPurpose(Number(raceDistance));
      // Duration-only sessions keep plannedKm: null (they are prescribed as
      // time), but carry an estimate so the week's volume is honest.
      if (s.plannedKm == null && s.plannedDuration != null) {
        const est = estimateKmFromDuration(s.type, s.plannedDuration, threshold.paceSec);
        if (est != null) s.estimatedKm = Math.round(est * 10) / 10;
      }
    }

    // The week's plannedKm is what the plan actually PRESCRIBES, i.e. the sum
    // of its sessions — not the ramp target. With the long run on its own
    // curve (§Pillar 3) and easy runs capped (§Pillar 1), a week with few
    // sessions cannot safely hold a big ramp target; publishing the target
    // anyway made the chart disagree with Weekly Load and adherence, which
    // read the sessions (§Pillar 5 rule 1).
    //
    // Duration-only sessions (time-on-feet, hike) contribute their ESTIMATE,
    // not a prescribed km. They used to contribute nothing, which understated
    // a 3-session Specific week by ~49 % — the chart showed the peak block
    // collapsing and Weekly Load called a correctly-run week 197 % of target.
    const prescribedKm = sessions.reduce(
      (a, x) => a + (Number(x.plannedKm) || Number(x.estimatedKm) || 0), 0);
    const weekKm = prescribedKm > 0 ? prescribedKm : plannedKm;

    weeks.push({
      weekNumber: phase.weekNumber,
      phase: phase.phase,
      isRecoveryWeek: !!phase.isRecoveryWeek,
      plannedKm: Math.round(weekKm * 10) / 10,
      // The ramp's target for this week (§Pillar 4 volume math). plannedKm is
      // what the sessions actually prescribe; the two differ when a week has
      // too few sessions to hold the target safely.
      rampTargetKm: Math.round(plannedKm * 10) / 10,
      plannedHours: plannedHoursOf(weekKm) != null ? Math.round(plannedHoursOf(weekKm) * 10) / 10 : null,
      plannedElevation,
      sessions,
    });
  }

  enforceQualityBuffer(weeks, threshold.paceSec);

  const plan = {
    planFormatVersion: PLAN_FORMAT_VERSION,
    raceDistance,
    raceDate,
    targetFinish: targetFinishSec != null ? formatHms(targetFinishSec) : null,
    terrain,
    elevationGain: Number(elevationGain) || 0,
    sessionsPerWeek,
    availableDays,
    longRunDay: (longRunDay && availableDays.includes(longRunDay)) ? longRunDay : null,
    tempoDay: (tempoDay && availableDays.includes(tempoDay)) ? tempoDay : null,
    includeBackToBack,
    includeHikes,
    includeVertical,
    goalMode: mode,
    generatedAt: new Date().toISOString(),
    // Pace model — what anchored the prescribed paces. Lets the UI honestly
    // label "paces from your Strava data" vs "projected from goal time".
    paceModel: {
      thresholdPaceSec: threshold.paceSec != null ? Math.round(threshold.paceSec) : null,
      thresholdSource: threshold.source,
    },
    // Readiness verdict — always attached, advisory once a plan is built.
    readiness,
    // Snapshot of the baseline the plan was built on (null for legacy callers).
    userBaseline: userBaseline || null,
    weeks,
  };

  if (mode === 'finish-only') {
    plan.finishNote =
      'Finish-focused plan. Your recent training supports completing the ' +
      'distance, not racing it — expect a conservative finish well ' +
      'outside the time you entered (commonly a 14–17 h range for a ' +
      '100K). Train the walk-run rhythm from week one, keep every long ' +
      'session easy, and practise race-day fuelling on each time-on-feet ' +
      'effort.';
  }
  // A standard plan that reached this point with readiness.ready === false
  // was an explicit acknowledged stretch — flag it so the UI keeps warning.
  if (mode === 'standard' && readiness.hasBaseline && !readiness.ready) {
    plan.acknowledgedStretch = true;
  }

  return plan;
}

module.exports = {
  generatePlan,
  isShortDistance,
  shortDistanceBand,
  intervalPurpose,
  assessReadiness,
  resolveThresholdPace,
  requiredStartVolume,
  finishOnlyPeakVolume,
  longRunCapKm,
  peakLongRunKm,
  computeLongRunTargets,
  RECOVERY_WEEK_FACTOR,
  LONG_RUN_FLOOR_KM,
  parseTargetFinishSec,
  formatHms,
  rampWeekCount,
  computePhases,
  computeWeeklyVolumes,
  computePeakVolume,
  baseSessionTypes,
  applyPhaseTypeModifiers,
  planPlacements,
  distributeSessions,
  weeklyElevationTarget,
  PLAN_FORMAT_VERSION,
  STANDARD_DISTANCES,
  SESSION_HR_ZONES,
  SESSION_PURPOSES,
  SESSION_THRESHOLD_MULTIPLIER,
  SESSION_TYPE_LABELS,
  formatSessionTypeLabel,
  thresholdPaceSecPerKm,
};
